import { Component, computed, inject, model, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { DrawerModule } from 'primeng/drawer';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../services/auth.service';
import { NotificationsBellComponent } from '../notifications-bell/notifications-bell.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, AsyncPipe, ButtonModule, MenuModule, DrawerModule, NotificationsBellComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  readonly mobileMenuOpen = model(false);

  private readonly currentUser = toSignal(this.authService.currentUser$);

  readonly isHandwerker = computed(() => this.currentUser()?.rolle === 'Handwerker');
  readonly isEndkunde = computed(() => this.currentUser()?.rolle === 'Endkunde');
  readonly isAdmin = computed(() => this.currentUser()?.rolle === 'Admin');
  readonly hasDashboard = computed(() => this.isHandwerker() || this.isEndkunde());

  readonly dashboardLabel = computed(() => {
    if (this.isHandwerker()) return 'Mein Bereich';
    if (this.isEndkunde()) return 'Meine Anfragen';
    return '';
  });

  /** First letter of the user's first name; falls back to first letter of email. */
  readonly initial = computed(() => {
    const u = this.currentUser();
    if (!u) return '';
    const src = u.vorname?.trim() || u.email;
    return src ? src.charAt(0).toUpperCase() : '';
  });

  readonly userMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];
    if (this.isEndkunde()) {
      items.push({
        label: 'Neue Anfrage stellen',
        icon: 'pi pi-plus',
        command: () => this.router.navigateByUrl('/anfrage/neu'),
      });
    } else if (this.isAdmin()) {
      items.push({
        label: 'Admin-Backoffice',
        icon: 'pi pi-shield',
        command: () => this.router.navigateByUrl('/admin'),
      });
    }
    if (this.isEndkunde() || this.isAdmin()) items.push({ separator: true });
    items.push({
      label: 'Mein Konto',
      icon: 'pi pi-user-edit',
      command: () => this.router.navigateByUrl('/konto'),
    });
    items.push({ separator: true });
    items.push({
      label: 'Abmelden',
      icon: 'pi pi-sign-out',
      command: () => this.authService.logout(),
    });
    return items;
  });

  readonly loginMenuItems: MenuItem[] = [
    {
      label: 'Als Kunde',
      icon: 'pi pi-user',
      command: () => this.router.navigateByUrl('/login'),
    },
    {
      label: 'Als Handwerker',
      icon: 'pi pi-wrench',
      command: () => this.router.navigateByUrl('/login/handwerker'),
    },
  ];

  readonly registerMenuItems: MenuItem[] = [
    {
      label: 'Als Kunde',
      icon: 'pi pi-user-plus',
      command: () => this.router.navigateByUrl('/registrieren'),
    },
    {
      label: 'Als Handwerker',
      icon: 'pi pi-briefcase',
      command: () => this.router.navigateByUrl('/registrieren/handwerker'),
    },
  ];

  openMobileMenu(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.mobileMenuOpen.set(true);
    }
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }
}
